import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HeaderNav } from "../components/HeaderNav";
import { Footer } from "../components/Footer";
import { Marquee } from "../components/Marquee";
import { RESUME_URL } from "../lib/data";
import {
  ArrowUpRight, Download, Home, Search, SlidersHorizontal, ThumbsUp, ThumbsDown,
  Sparkles, Paperclip, MessageSquare, Share2, Map as MapIcon, Table2, ChevronDown,
  FileText, Users, Shield, Layers, Copy, Info, Building2, Activity,
} from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.span>
  </span>
);

const Chapter = ({ n, label, title, children }) => (
  <section className="max-w-5xl mx-auto px-6 py-14 border-t border-black/5">
    <motion.div {...reveal} className="grid md:grid-cols-12 gap-8">
      <div className="md:col-span-4">
        <div className="flex items-baseline gap-4">
          <span className="font-serif text-5xl text-primary/20 leading-none">{n}</span>
          <span className="overline text-primary">{label}</span>
        </div>
        {title && <h2 className="font-serif text-3xl md:text-4xl text-foreground mt-5 leading-tight">{title}</h2>}
      </div>
      <div className="md:col-span-7 md:col-start-6 text-lg text-foreground/70 leading-relaxed space-y-4">{children}</div>
    </motion.div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((p) => (
      <li key={p} className="flex gap-3 text-base"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" /><span>{p}</span></li>
    ))}
  </ul>
);

/* ---------- Anonymized browser-framed screen ---------- */
const Browser = ({ title = "AI Insights Hub", tab = "Chat", children, caption }) => (
  <div>
    <div className="rounded-2xl overflow-hidden border border-black/10 bg-white card-shadow">
      <div className="flex items-center gap-2 bg-[#F1F1F4] px-4 py-2.5 border-b border-black/5">
        <span className="h-3 w-3 rounded-full bg-[#FF5F57]" /><span className="h-3 w-3 rounded-full bg-[#FEBC2E]" /><span className="h-3 w-3 rounded-full bg-[#28C840]" />
        <div className="ml-3 flex-1 max-w-md rounded-md bg-white border border-black/5 px-3 py-1 text-[11px] text-foreground/40 font-mono">app.insights-hub.internal</div>
      </div>
      {/* App top nav */}
      <div className="flex items-center justify-between px-5 h-11 border-b border-black/5">
        <span className="inline-flex items-center gap-2 text-[#0460A9] font-semibold text-sm"><Sparkles size={15} /> {title}</span>
        <div className="hidden sm:flex items-center gap-5 text-[11px] text-foreground/50">
          {["Chat", "File", "Users", "Filters"].map((t) => (
            <span key={t} className={t === tab || (tab === "Chat" && t === "Chat") ? "text-[#0460A9] font-medium border-b-2 border-[#0460A9] pb-1" : ""}>{t}</span>
          ))}
        </div>
      </div>
      {children}
    </div>
    {caption && <div className="mt-3 text-center text-[11px] font-mono uppercase tracking-widest text-foreground/40">{caption}</div>}
  </div>
);

const FilterRail = ({ active = [] }) => (
  <aside className="w-40 shrink-0 bg-[#0460A9] text-white p-3 text-[10px]">
    <div className="font-semibold text-xs mb-3">Research Filter</div>
    <div className="rounded-lg bg-white/10 p-2 mb-2">
      <div className="flex items-center justify-between mb-1.5"><span>Active Filters</span><span className="rounded-full bg-[#EC9A1E] px-1.5">{active.length}</span></div>
      {active.length === 0 ? <div className="text-white/60">No active filters</div> : (
        <div className="flex flex-wrap gap-1">{active.map((a) => <span key={a} className="rounded-full bg-white/20 px-1.5 py-0.5">{a}</span>)}</div>
      )}
    </div>
    {["Data Source", "Therapy Area", "Indication", "Specialty", "Territory", "Date Range"].map((f) => (
      <div key={f} className="flex items-center justify-between rounded-md bg-white/5 px-2 py-1.5 mb-1"><span>{f}</span><ChevronDown size={11} /></div>
    ))}
  </aside>
);

const ScreenConversation = () => (
  <div className="flex h-[420px]">
    <FilterRail active={[]} />
    <div className="flex-1 bg-[#F7F8FA] p-5 flex flex-col">
      <div className="text-center">
        <div className="font-serif text-lg text-foreground">Your intelligence assistant</div>
        <div className="text-xs text-foreground/50 mt-1">Ask a question, or start from a suggestion</div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2.5 flex-1">
        {[
          "What are the key insights for this healthcare professional?",
          "Summarise prescribing trends from the past six months.",
          "What factors may be influencing treatment behaviour?",
          "Summarise recent field interactions.",
          "How has the account changed vs. the previous period?",
          "Which HCPs are most connected in this network?",
        ].map((q) => (
          <div key={q} className="rounded-xl bg-white border border-black/5 p-3 text-[11px] text-foreground/60 leading-snug hover:border-[#0460A9]/40 transition-colors">{q}</div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-full bg-white border border-black/10 px-3 py-2">
        <Paperclip size={14} className="text-foreground/40" />
        <span className="text-[11px] text-foreground/40 flex-1">Ask about an HCP, account or network…</span>
        <span className="rounded-full bg-[#0460A9] text-white px-3 py-1 text-[10px]">Ask</span>
      </div>
    </div>
  </div>
);

const ScreenResponse = () => (
  <div className="flex h-[420px]">
    <FilterRail active={["Cardiology", "Territory 4", "Q3–Q4"]} />
    <div className="flex-1 bg-[#F7F8FA] p-5 overflow-hidden">
      <div className="flex justify-end mb-3">
        <div className="rounded-2xl rounded-tr-sm bg-[#E7EEF7] text-foreground text-[11px] px-3 py-2 max-w-[70%]">Summarise prescribing trends for this account over the past six months.</div>
      </div>
      <div className="rounded-2xl rounded-tl-sm bg-white border border-black/5 p-4 text-[11px] text-foreground/75 leading-relaxed">
        <p>Prescribing for this account increased steadily across the period, led by two high-volume specialists. Referral inflow from the regional network also rose.</p>
        <div className="mt-2 font-semibold text-foreground">Key signals</div>
        <ul className="mt-1 space-y-1">
          <li>• Volume up vs. prior period <span className="text-[#0460A9]">[1]</span></li>
          <li>• Strong in-network referrals <span className="text-[#0460A9]">[2]</span></li>
          <li>• Two emerging prescribers identified <span className="text-[#0460A9]">[3]</span></li>
        </ul>
        <div className="mt-3 flex items-center justify-between border-t border-black/5 pt-2 text-foreground/40">
          <span className="inline-flex items-center gap-1"><Info size={11} /> Sources · Timeframe · Disclaimer</span>
          <span className="flex items-center gap-2"><Copy size={12} /><ThumbsUp size={12} /><ThumbsDown size={12} /></span>
        </div>
      </div>
      <div className="mt-3 rounded-lg bg-[#EEF4FB] border border-[#0460A9]/15 px-3 py-2 text-[10px] text-[#0460A9] inline-flex items-center gap-1.5">
        <Shield size={11} /> AI-assisted · verify against source before acting
      </div>
    </div>
  </div>
);

const ScreenDashboard = () => (
  <div className="h-[420px] bg-[#F7F8FA] p-5 overflow-hidden">
    <div className="flex items-center justify-between">
      <div>
        <div className="font-serif text-base text-foreground">Dr. A. Rivera — HCP Intelligence</div>
        <div className="text-[10px] text-foreground/50 font-mono">Cardiology · Metro Health System · Territory 4</div>
      </div>
      <span className="rounded-full bg-white border border-black/10 px-2.5 py-1 text-[10px] text-foreground/50 inline-flex items-center gap-1"><SlidersHorizontal size={11} /> Filters</span>
    </div>
    <div className="mt-4 grid grid-cols-4 gap-2.5">
      {[["Patients", "1,284"], ["Referrals in", "312"], ["Referrals out", "97"], ["Trend", "+8%"]].map(([l, v]) => (
        <div key={l} className="rounded-xl bg-white border border-black/5 p-3"><div className="font-serif text-xl text-foreground">{v}</div><div className="text-[10px] text-foreground/50 font-mono uppercase">{l}</div></div>
      ))}
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2.5">
      <div className="col-span-2 rounded-xl bg-white border border-black/5 p-3">
        <div className="text-[10px] text-foreground/50 font-mono uppercase mb-2">Prescribing over time</div>
        <div className="flex items-end gap-1.5 h-20">
          {[40, 55, 48, 62, 70, 66, 78, 84].map((h, i) => <div key={i} className="flex-1 rounded-t bg-[#0460A9]/70" style={{ height: `${h}%` }} />)}
        </div>
      </div>
      <div className="rounded-xl bg-[#0A0A0C] text-white p-3">
        <div className="text-[10px] text-[#A5B4FC] font-mono uppercase mb-1.5 flex items-center gap-1"><Sparkles size={11} /> AI key insight</div>
        <p className="text-[10px] text-white/70 leading-relaxed">Growth is concentrated in two indications; referral strength suggests expansion potential.</p>
      </div>
    </div>
  </div>
);

const ScreenNetwork = () => (
  <div className="h-[420px] bg-[#0A0A0C] p-5 relative overflow-hidden">
    <div className="flex items-center justify-between relative z-10">
      <div className="text-white font-serif text-base">Referral Network</div>
      <div className="flex gap-1.5 text-[10px]">
        {[[Share2, "Network"], [MapIcon, "Map"], [Table2, "Table"]].map(([Ic, l], i) => (
          <span key={l} className={`inline-flex items-center gap-1 rounded-full px-2 py-1 ${i === 0 ? "bg-[#0460A9] text-white" : "bg-white/10 text-white/60"}`}><Ic size={11} /> {l}</span>
        ))}
      </div>
    </div>
    <svg viewBox="0 0 500 320" className="absolute inset-0 top-12 w-full h-[300px]">
      <g stroke="#6366F1" strokeOpacity="0.4" strokeWidth="1.5">
        <line x1="250" y1="160" x2="120" y2="80" /><line x1="250" y1="160" x2="400" y2="90" />
        <line x1="250" y1="160" x2="110" y2="240" /><line x1="250" y1="160" x2="410" y2="250" />
        <line x1="250" y1="160" x2="250" y2="60" /><line x1="120" y1="80" x2="110" y2="240" />
      </g>
      <g>
        {[[250, 160, 13], [120, 80, 7], [400, 90, 7], [110, 240, 7], [410, 250, 7], [250, 60, 6]].map(([x, y, r], i) => (
          <circle key={i} cx={x} cy={y} r={r} fill={i === 0 ? "#A5B4FC" : "#6366F1"} />
        ))}
      </g>
    </svg>
    <div className="absolute bottom-4 left-5 right-5 z-10 rounded-xl bg-white/5 border border-white/10 p-3 backdrop-blur">
      <div className="text-white text-[11px] font-medium">Metro Health System</div>
      <div className="text-white/50 text-[10px] mt-0.5">In-network · 312 patients · 6 connected HCPs · Territory 4</div>
    </div>
  </div>
);

const SCREENS = [
  { c: <ScreenConversation />, caption: "Conversational workspace · prompt suggestions", wide: true },
  { c: <ScreenResponse />, caption: "AI response · sources, disclaimer & feedback", wide: true },
  { c: <ScreenDashboard />, caption: "HCP intelligence dashboard", wide: true },
  { c: <ScreenNetwork />, caption: "Referral-network visualisation", wide: true },
];

const INFO = [
  ["Client", "Global pharmaceutical company"],
  ["Product", "AI Insights Hub"],
  ["Platform", "Enterprise web application"],
  ["Industry", "Healthcare & life sciences"],
  ["Role", "Product / UX Designer"],
  ["Duration", "3 months"],
];

const CHALLENGES = [
  ["Fragmented information", "Prescribing data, patient activity, account relationships, field notes and referral patterns lived in different places — no unified view of an HCP or account."],
  ["Complex data exploration", "Users needed to move fluidly between account-, HCP- and network-level information while keeping the context of their original question."],
  ["High information density", "Referral networks, eligibility, prescribing and time-based comparisons carry huge amounts of information that must be surfaced without overwhelm."],
  ["Limited trust in AI answers", "In enterprise healthcare, an answer can't be an unsupported recommendation. Users need its source, scope, timeframe and limitations."],
  ["Multiple roles & use cases", "Field, medical and insight teams need different depth — quick pre-engagement prep and deeper analytical exploration alike."],
];

const TENETS = [
  [FileText, "Explainable by default", "Every response can expose its sources, timeframe and a usage disclaimer — AI positioned as support, not absolute guidance."],
  [Layers, "Progressive disclosure", "Summary-first cards, collapsible sections, tabs, drawers and detail-on-demand tables keep complexity accessible."],
  [SlidersHorizontal, "Filters as comprehension", "Active filters stay visible so users always know which data is shaping a chart or answer."],
  [Users, "Context that persists", "The selected HCP, account, filters and timeframe carry across dashboards and conversation."],
];

export default function AIInsightsHubCaseStudy() {
  useEffect(() => {
    document.title = "AI Insights Hub — Case Study · Vatushi Moondra";
    return () => { document.title = "Vatushi Moondra — Senior UX Designer"; };
  }, []);

  return (
    <div className="relative bg-background">
      <HeaderNav overDark />

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden bg-[#0A0A0C] pt-28 pb-16">
        <div className="absolute inset-0" style={{ background: "radial-gradient(55% 60% at 25% 30%, rgba(99,102,241,0.28), transparent 70%)" }} />
        <svg className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1200 800">
          <g stroke="#6366F1" strokeOpacity="0.35" strokeWidth="1">
            <line x1="900" y1="140" x2="1050" y2="300" /><line x1="900" y1="140" x2="760" y2="260" />
            <line x1="1050" y1="300" x2="980" y2="480" /><line x1="760" y1="260" x2="980" y2="480" />
          </g>
          <g fill="#A5B4FC">
            <circle cx="900" cy="140" r="6" /><circle cx="1050" cy="300" r="5" /><circle cx="760" cy="260" r="4" /><circle cx="980" cy="480" r="5" />
          </g>
        </svg>
        <div className="relative z-10 max-w-6xl mx-auto px-6 w-full">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A5B4FC]">
            Enterprise · Healthcare AI · Anonymized case study
          </motion.span>
          <h1 className="mt-6 font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.0] tracking-tight">
            <MaskLine delay={0.15}>From a question</MaskLine>
            <MaskLine delay={0.28}>to an <span className="text-[#A5B4FC] italic">evidence-backed</span></MaskLine>
            <MaskLine delay={0.41}>insight.</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 max-w-2xl text-white/70 text-lg leading-relaxed">
            An intelligence workspace for a global pharmaceutical company — unifying research,
            prescribing, referral and account data into a single trusted, AI-assisted experience.
          </motion.p>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-black/5 bg-secondary/40 py-5 font-mono text-sm uppercase tracking-[0.18em] text-foreground/70">
        <Marquee items={["Information Architecture", "AI Conversation", "Dashboards", "Referral Networks", "Explainable AI", "Design System"]} />
      </div>

      {/* Project info */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {INFO.map(([k, v], i) => (
            <motion.div key={k} {...reveal} transition={{ ...reveal.transition, delay: i * 0.05 }}>
              <div className="overline text-primary/70">{k}</div>
              <div className="text-foreground mt-1.5 text-sm leading-snug">{v}</div>
            </motion.div>
          ))}
        </div>
        <div className="mt-6 text-sm text-foreground/50">
          <span className="font-medium text-foreground/70">Scope:</span> IA · user flows · wireframing · interaction design · UI · prototyping · design-system development.
          <span className="ml-2 font-medium text-foreground/70">Primary users:</span> field representatives, medical teams, insight teams, regional leadership.
        </div>
      </section>

      <Chapter n="01" label="Overview" title="An intelligence workspace, not another dashboard.">
        <p>A global pharmaceutical company was building a scalable AI platform to help teams access business and medical insights without navigating multiple disconnected systems.</p>
        <p>It brought together qualitative research, quantitative data, field interactions, prescribing behaviour, referral activity and account-level intelligence in one enterprise experience. I designed an integrated platform combining natural-language AI conversation, HCP and account search, prescribing and patient insights, referral-network visualisation, pre-engagement planning, contextual filters and comparisons, evidence and feedback controls, and administrative tools.</p>
        <p>The objective: move beyond static dashboards to a workspace where users explore data, ask follow-up questions, and understand the evidence behind every insight.</p>
      </Chapter>

      <Chapter n="02" label="Design Problem" title="Make complex intelligence easy to access — without oversimplifying it.">
        <p>Medical and field teams worked with information distributed across several systems and formats. Preparing for an engagement meant manually searching, comparing and interpreting from many sources.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          {CHALLENGES.map(([t, d]) => (
            <div key={t} className="rounded-2xl border border-black/5 bg-white p-4 card-shadow">
              <div className="font-serif text-lg text-foreground">{t}</div>
              <p className="text-sm text-foreground/60 mt-1.5 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <div className="rounded-2xl bg-[#0A0A0C] text-white p-6 mt-3">
          <div className="overline text-[#A5B4FC] mb-2">How might we</div>
          <p className="font-serif text-xl md:text-2xl leading-snug">…create a trusted AI-assisted workspace that helps healthcare teams move from a question to an evidence-backed insight without searching across multiple systems?</p>
        </div>
      </Chapter>

      <Chapter n="03" label="Process" title="An iterative enterprise-product process.">
        <p><span className="font-medium text-foreground">Existing-experience audit.</span> The earlier experience was heavily form-based — users configured several fields before an answer appeared, with dense, text-heavy results and little support for exploration or follow-up.</p>
        <p><span className="font-medium text-foreground">Requirements & use-case mapping.</span> Searching for an HCP or account, preparing for engagements, natural-language Q&amp;A, prompt suggestions, conversation history, prescribing/patient summaries, referral exploration, comparisons, contextual filtering, response feedback, and administration — organised into one shared architecture, not independent dashboards.</p>
        <p><span className="font-medium text-foreground">Information architecture.</span> Three primary layers:</p>
        <Bullets items={[
          "Entity selection — search for an HCP or account",
          "Insight exploration — structured summaries, charts and networks",
          "Conversational investigation — ask and follow up without losing context",
        ]} />
        <p><span className="font-medium text-foreground">Iteration & cross-functional review.</span> The file carried multiple workflow states and separate annotations for design, development and business stakeholders — so behaviour, data needs and edge cases were reviewed before handoff.</p>
      </Chapter>

      <Chapter n="04" label="Wireframes" title="Architecture first, visuals later.">
        <p>Wireframes established the product architecture and validated the relationship between search, dashboards and AI conversation before applying the visual system.</p>
        <Bullets items={[
          "Entity search — name, type, identifiers, recent entities and previous conversations, so answers always attach to the correct entity",
          "Conversational workspace — persistent composer, suggested prompts, history, bookmarks, responses, follow-ups, sources and feedback",
          "Insight dashboards — summary → key metrics → AI insight cards → charts → referral networks → detailed tables → contextual chat",
          "Filter & comparison flows — default, open, applied, custom, account vs. account, period vs. period, reset",
          "Safety & feedback flows — intended-use acknowledgement, limitations, sources, disclaimers, thumbs feedback and optional written feedback",
        ]} />
      </Chapter>

      {/* UI — Interface gallery */}
      <section className="border-t border-black/5 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="max-w-3xl mb-10">
            <div className="flex items-baseline gap-4">
              <span className="font-serif text-5xl text-primary/20 leading-none">05</span>
              <span className="overline text-primary">UI Design · Interface</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-5 leading-tight">The product, screen by screen.</h2>
            <p className="mt-4 text-lg text-foreground/70 leading-relaxed">
              Key flows recreated and fully anonymized for portfolio presentation — conversation,
              explainable responses, entity intelligence and the referral network.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6" data-testid="aihub-interface-gallery">
            {SCREENS.map((s, i) => (
              <motion.div key={i} data-testid="aihub-screen"
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}>
                <Browser caption={s.caption}>{s.c}</Browser>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Design principles */}
      <section className="max-w-6xl mx-auto px-6 py-14 border-t border-black/5">
        <motion.div {...reveal} className="max-w-3xl mb-10">
          <span className="overline text-primary">Interaction principles</span>
          <h2 className="font-serif text-3xl md:text-4xl text-foreground mt-3 leading-tight">Trust, built into the workflow.</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TENETS.map(([Ic, t, d], i) => (
            <motion.div key={t} {...reveal} transition={{ ...reveal.transition, delay: i * 0.06 }}
              className="rounded-2xl border border-black/5 bg-white p-5 card-shadow">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Ic size={18} /></span>
              <h3 className="font-serif text-lg text-foreground mt-4">{t}</h3>
              <p className="text-sm text-foreground/60 mt-2 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Design system */}
      <section className="border-t border-black/5 bg-secondary/40 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="max-w-3xl mb-10">
            <span className="overline text-primary">Design system</span>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mt-3 leading-tight">One system across chat, analytics & admin.</h2>
            <p className="mt-4 text-foreground/70 leading-relaxed">A component-led foundation — so the platform can extend to new data domains and AI agents without redesigning its patterns.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="rounded-2xl bg-white border border-black/5 p-5 card-shadow">
              <div className="overline text-primary/70 mb-3">Semantic colour roles</div>
              <div className="grid grid-cols-5 gap-1.5">
                {["#023761", "#0460A9", "#5291DD", "#9ABFDC", "#EDF3F9"].map((c) => <div key={c} className="h-9 rounded-md" style={{ background: c }} />)}
              </div>
              <div className="mt-2 text-[11px] text-foreground/40 font-mono">Primary · info · states · surfaces</div>
            </div>
            <div className="rounded-2xl bg-white border border-black/5 p-5 card-shadow">
              <div className="overline text-primary/70 mb-3">Components</div>
              <div className="flex flex-wrap gap-1.5">
                {["Buttons", "Inputs", "Dropdowns", "Tables", "Cards", "Tabs", "Charts", "Dialogs", "Feedback", "Empty states"].map((c) => (
                  <span key={c} className="rounded-full bg-secondary text-secondary-foreground font-mono text-[10px] px-2.5 py-1 border border-black/5">{c}</span>
                ))}
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-black/5 p-5 card-shadow">
              <div className="overline text-primary/70 mb-3">Coverage</div>
              <Bullets items={["Conversational patterns", "Analytical dashboards", "Administration & config"]} />
            </div>
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...reveal} className="rounded-3xl bg-[#0A0A0C] text-white p-10 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0" style={{ background: "radial-gradient(60% 60% at 80% 20%, rgba(99,102,241,0.22), transparent 70%)" }} />
            <div className="relative z-10">
              <span className="overline text-[#A5B4FC]">06 · Impact</span>
              <h2 className="font-serif text-3xl md:text-5xl mt-4 max-w-3xl leading-tight">A unified framework for accessing and interpreting intelligence.</h2>
              <div className="mt-8 grid md:grid-cols-2 gap-x-10 gap-y-3 text-white/70">
                {[
                  "Reduced movement between disconnected reports and systems",
                  "Shortened the path from entity search to actionable insight",
                  "Combined structured dashboards with natural-language exploration",
                  "Made complex referral & patient relationships easier to interpret",
                  "Preserved context across search, filtering, analysis & conversation",
                  "Improved confidence in AI outputs via visible sources & disclaimers",
                  "Supported multiple roles through progressive levels of detail",
                  "Created a scalable foundation for additional intelligence modules",
                ].map((p) => (
                  <div key={p} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#A5B4FC]" /><span className="text-sm leading-relaxed">{p}</span></div>
                ))}
              </div>
              <p className="mt-6 text-white/40 text-xs">Described as product & usability outcomes — the file contains no verified production metrics, so no quantified results are claimed.</p>
            </div>
          </motion.div>
        </div>
      </section>

      <Chapter n="07" label="Learnings">
        <Bullets items={[
          "AI trust must be designed in — sources, limits and feedback belong in the core workflow, not a footer.",
          "Conversational and analytical modes serve different needs; together they beat either alone.",
          "Context (HCP, account, filters, timeframe) must persist across dashboards and chat.",
          "Progressive disclosure is essential for dense enterprise dashboards.",
          "Filters explain what you're viewing — keeping them visible prevents misreading a subset.",
          "Feedback should be low-effort: quick thumbs, with optional written detail.",
          "Scalable AI products need reusable interaction patterns for search, prompting, evidence and feedback.",
        ]} />
      </Chapter>

      {/* Confidentiality */}
      <div className="max-w-5xl mx-auto px-6 pb-4">
        <div className="rounded-2xl bg-secondary px-5 py-4 text-sm text-foreground/70 flex items-start gap-3">
          <Shield size={18} className="text-primary shrink-0 mt-0.5" />
          <span>This case study is fully anonymized. The client is referred to only as a global pharmaceutical company, all screens and data are recreated with generic placeholders, and no confidential names, brands or metrics are shown.</span>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-5xl mx-auto px-6 mt-10 mb-6 flex flex-wrap items-center justify-between gap-6 border-t border-black/5 pt-10">
        <Link to="/" data-testid="aihub-home-cta" className="inline-flex items-center gap-2 text-primary hover:underline font-medium">
          <Home size={16} /> Back to home
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/#projects" className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-5 py-2.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors">More projects <ArrowUpRight size={15} /></Link>
          <a href={RESUME_URL} target="_blank" rel="noopener noreferrer" download
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-[hsl(242,47%,28%)] transition-colors"><Download size={15} /> Download Resume</a>
        </div>
      </div>

      <Footer />
    </div>
  );
}
